import React, { useState } from 'react';

const INCIDENT_TYPES = ['Censorship', 'Cultural erasure', 'Academic suppression', 'Media pressure', 'Legislative', 'Institutional capture', 'Blacklisting', 'Journalist targeting', 'Media suppression'];
const INCIDENT_STATES = ['Currently in effect', 'Overturned', 'Developing'];
const COUNTRIES = [
  { name: 'Global', code: 'GLOBAL' }, { name: 'Israeli state', code: 'IL' },
  { name: 'United States', code: 'US' }, { name: 'United Kingdom', code: 'GB' },
  { name: 'Canada', code: 'CA' }, { name: 'Germany', code: 'DE' },
  { name: 'Australia', code: 'AU' }, { name: 'France', code: 'FR' },
];

function slugify(text) {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

export default function IncidentForm({ incident = null, orgs = [], onSave, onClose }) {
  const isEdit = !!incident;
  const [form, setForm] = useState({
    title: incident?.title || '',
    date: incident?.date || '',
    country: incident?.country || '',
    countryCode: incident?.countryCode || '',
    institution: incident?.institution || '',
    institutionType: incident?.institutionType || '',
    description: incident?.description || '',
    pressuredBy: incident?.pressuredBy || [],
    pressureDetail: incident?.pressureDetail || '',
    currentState: incident?.currentState || '',
    tags: incident?.tags || [],
    response: incident?.response || '',
    sources: incident?.sources || [],
    relatedOrgs: incident?.relatedOrgs || [],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [orgSearch, setOrgSearch] = useState('');

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleCountryChange = (name) => {
    const found = COUNTRIES.find(c => c.name === name);
    set('country', name);
    if (found) set('countryCode', found.code);
  };

  const toggleTag = (tag) => {
    set('tags', form.tags.includes(tag) ? form.tags.filter(t => t !== tag) : [...form.tags, tag]);
  };

  const toggleOrg = (orgId, field) => {
    set(field, form[field].includes(orgId) ? form[field].filter(id => id !== orgId) : [...form[field], orgId]);
  };

  const addSource = () => set('sources', [...form.sources, { name: '', url: '' }]);
  const updateSource = (idx, field, value) => {
    const updated = [...form.sources];
    updated[idx] = { ...updated[idx], [field]: value };
    set('sources', updated);
  };
  const removeSource = (idx) => set('sources', form.sources.filter((_, i) => i !== idx));

  const filteredOrgs = orgSearch.trim()
    ? orgs.filter(o => o.name.toLowerCase().includes(orgSearch.toLowerCase()))
    : orgs.slice(0, 20);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { setError('Title is required'); return; }
    if (!form.description.trim()) { setError('Description is required'); return; }

    setSaving(true);
    setError('');
    try {
      const data = {
        ...form,
        slug: incident?.slug || slugify(form.title),
        id: incident?.id || slugify(form.title),
        sources: form.sources.filter(s => s.name.trim() || s.url.trim()),
      };
      await onSave(data);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-modal-backdrop" onClick={onClose}>
      <div className="admin-modal admin-modal-wide" onClick={e => e.stopPropagation()}>
        <div className="admin-modal-header">
          <h3>{isEdit ? 'Edit Incident' : 'New Incident'}</h3>
          <button className="admin-modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="admin-modal-body">
            {error && <div className="admin-error">{error}</div>}

            <label className="admin-label">Title *</label>
            <input className="admin-input" value={form.title} onChange={e => set('title', e.target.value)} placeholder="Incident title" />

            <div className="admin-row">
              <div className="admin-field">
                <label className="admin-label">Date</label>
                <input type="date" className="admin-input" value={form.date} onChange={e => set('date', e.target.value)} />
              </div>
              <div className="admin-field">
                <label className="admin-label">Country</label>
                <select className="admin-input" value={form.country} onChange={e => handleCountryChange(e.target.value)}>
                  <option value="">Select...</option>
                  {COUNTRIES.map(c => <option key={c.code} value={c.name}>{c.name}</option>)}
                </select>
              </div>
              <div className="admin-field">
                <label className="admin-label">Status</label>
                <select className="admin-input" value={form.currentState} onChange={e => set('currentState', e.target.value)}>
                  <option value="">Select...</option>
                  {INCIDENT_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            <div className="admin-row">
              <div className="admin-field">
                <label className="admin-label">Institution</label>
                <input className="admin-input" value={form.institution} onChange={e => set('institution', e.target.value)} placeholder="e.g. University of X" />
              </div>
              <div className="admin-field">
                <label className="admin-label">Institution type</label>
                <select className="admin-input" value={form.institutionType} onChange={e => set('institutionType', e.target.value)}>
                  <option value="">Select...</option>
                  {INCIDENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>

            <label className="admin-label">Description *</label>
            <textarea className="admin-input" value={form.description} onChange={e => set('description', e.target.value)} rows={4} placeholder="What happened..." />

            <label className="admin-label">Pressure detail</label>
            <textarea className="admin-input" value={form.pressureDetail} onChange={e => set('pressureDetail', e.target.value)} rows={2} placeholder="How pressure was applied..." />

            <label className="admin-label">Response and pushback</label>
            <textarea className="admin-input" value={form.response} onChange={e => set('response', e.target.value)} rows={2} placeholder="How people responded..." />

            <label className="admin-label">Tags</label>
            <div className="admin-tags-grid">
              {INCIDENT_TYPES.map(tag => (
                <button key={tag} type="button" className={`admin-tag-option ${form.tags.includes(tag) ? 'active' : ''}`} onClick={() => toggleTag(tag)}>
                  {tag}
                </button>
              ))}
            </div>

            <label className="admin-label">Pressured by (organisations)</label>
            <input className="admin-input" value={orgSearch} onChange={e => setOrgSearch(e.target.value)} placeholder="Search organisations..." style={{marginBottom: '0.5rem'}} />
            <div className="admin-org-picker">
              {filteredOrgs.map(o => (
                <button key={o.id} type="button" className={`admin-tag-option ${form.pressuredBy.includes(o.id) ? 'active' : ''}`} onClick={() => toggleOrg(o.id, 'pressuredBy')}>
                  {o.name}
                </button>
              ))}
            </div>

            <label className="admin-label">Related organisations</label>
            <div className="admin-org-picker">
              {filteredOrgs.map(o => (
                <button key={o.id} type="button" className={`admin-tag-option ${form.relatedOrgs.includes(o.id) ? 'active' : ''}`} onClick={() => toggleOrg(o.id, 'relatedOrgs')}>
                  {o.name}
                </button>
              ))}
            </div>

            <label className="admin-label">Sources</label>
            {form.sources.map((s, idx) => (
              <div key={idx} className="admin-source-row">
                <input className="admin-input" value={s.name} onChange={e => updateSource(idx, 'name', e.target.value)} placeholder="Source name" />
                <input className="admin-input" value={s.url} onChange={e => updateSource(idx, 'url', e.target.value)} placeholder="https://..." />
                <button type="button" className="admin-btn-remove" onClick={() => removeSource(idx)}>×</button>
              </div>
            ))}
            <button type="button" className="admin-btn-add" onClick={addSource}>+ Add source</button>
          </div>
          <div className="admin-modal-footer">
            <button type="button" className="admin-btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="admin-btn-primary" disabled={saving}>
              {saving ? 'Saving...' : (isEdit ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
